import { CircleXIcon, PasteIcon } from '@/components/atoms/icons';
import { useTranslation, useUIColors } from '@/core/hooks';
import { logger } from '@/core/logger';
import * as Clipboard from 'expo-clipboard';
import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Modal, Pressable, StyleSheet, Text, TextInput, TouchableOpacity, View, Platform } from 'react-native';

interface OTPInputProps {
    length?: number;
    onComplete?: (otp: string) => void;
    /** Web only: when OTP is full, Enter triggers this (e.g. Emirates ID login verify). */
    onEnterSubmit?: () => void;
    onChangeText?: (otp: string) => void;
    onFocus?: () => void;
    label?: string;
    errorMessage?: string;
    /** Overrides the default theme error text color (colors.fontErrorText) for this instance only. */
    errorTextColor?: string;
    successMessage?: string;
    disabled?: boolean;
    loading?: boolean; // Loading state - shows spinner and prevents editing
    autoFocus?: boolean;
    value?: string; // external control (passcode mode)
    secure?: boolean; // mask digits (passcode mode)
    focusable?: boolean; // disable keyboard popup
    type?: 'phone' | 'email'; // Type of OTP (for SMS reading on phone type)
    /** Box width/height in px — defaults to 58 (existing size), pass smaller to compress the whole input. */
    boxSize?: number;
    /** Gap between boxes in px — defaults to 24 (existing gap), pass smaller to compress the whole input. */
    gap?: number;
    /** Box corner radius in px — defaults to 16 (existing radius). */
    boxRadius?: number;
    /** Digit font size in px — defaults to 24 (existing size). */
    fontSize?: number;
}

export const OTPInput: React.FC<OTPInputProps> = ({
    length = 4,
    onComplete,
    onEnterSubmit,
    onChangeText,
    onFocus,
    label,
    errorMessage,
    errorTextColor,
    successMessage,
    disabled = false,
    loading = false,
    autoFocus = true,
    value,
    secure = false,
    focusable = true,
    type = 'phone',
    boxSize = 58,
    gap = 24,
    boxRadius = 16,
    fontSize = 24,
}) => {
    const colors = useUIColors();
    const { t } = useTranslation();
    const [otp, setOtp] = useState(value || '');
    const [state, setState] = useState<'default' | 'loading' | 'success' | 'error'>('default');
    const [isFocused, setIsFocused] = useState(false);
    /** Controls the hidden TextInput's native cursor/selection so tapping a specific
     * box can select that digit for in-place editing, instead of always jumping to
     * the end. Kept in sync with real native cursor moves via onSelectionChange. */
    const [selection, setSelection] = useState({ start: 0, end: 0 });
    const inputRef = useRef<TextInput | null>(null);
    /** Blocks duplicate Enter submits until loading ends or OTP is edited (web + onEnterSubmit only). */
    const enterSubmitGuardRef = useRef(false);
    const prevLoadingRef = useRef(loading);
    const [contextMenuVisible, setContextMenuVisible] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (prevLoadingRef.current && !loading) {
            enterSubmitGuardRef.current = false;
        }
        prevLoadingRef.current = loading;
    }, [loading]);

    useEffect(() => {
        if (otp.length < length) {
            enterSubmitGuardRef.current = false;
        }
    }, [otp.length, length]);

    // Web-only Enter → verify when parent opts in (Emirates ID login OTP).
    // Do not require isFocused: completing the last digit calls Keyboard.dismiss(),
    // which blurs the hidden input and would otherwise remove this listener.
    useEffect(() => {
        if (
            Platform.OS !== 'web' ||
            typeof window === 'undefined' ||
            !onEnterSubmit
        ) {
            return undefined;
        }

        const handleWindowKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Enter') {
                return;
            }
            if (disabled || loading || otp.length !== length || enterSubmitGuardRef.current) {
                return;
            }
            event.preventDefault();
            enterSubmitGuardRef.current = true;
            onEnterSubmit();
        };

        window.addEventListener('keydown', handleWindowKeyDown);
        return () => window.removeEventListener('keydown', handleWindowKeyDown);
    }, [otp, disabled, loading, onEnterSubmit, length]);

    useEffect(() => {
        // The input is non-editable while loading, so focus() is ignored then —
        // wait for loading to finish instead of silently missing the autofocus.
        if (autoFocus && !disabled && !loading && focusable) {
            // Add a small delay to ensure the screen is fully mounted and ready
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 300);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [autoFocus, disabled, loading, focusable]);

    // Sync external value - always sync when value prop changes, but preserve during loading
    useEffect(() => {
        if (typeof value === 'string') {
            // Always sync if value is provided and different
            // When loading, only update if value is same length or longer (don't clear, but allow updates)
            if (value !== otp) {
                if (!loading) {
                    // Normal case: always sync
                    setOtp(value);
                    setSelection({ start: value.length, end: value.length });
                } else {
                    // Loading case: only update if preserving or adding digits (never clear)
                    if (value.length >= otp.length) {
                        setOtp(value);
                        setSelection({ start: value.length, end: value.length });
                    }
                    // If loading and value is shorter, preserve current value (don't sync)
                }
            }
        } else if (value === undefined && !loading) {
            // Only clear if value is explicitly undefined and not loading
            // This allows parent to control clearing, but preserves during loading
            if (otp.length > 0) {
                // Don't auto-clear during loading
            }
        }
    }, [value, otp, loading]);

    // Set state based on props: loading > error > success > default
    // Success and error states are ONLY controlled by props (successMessage/errorMessage)
    // No auto-success - parent component must explicitly provide successMessage prop
    useEffect(() => {
        if (loading) {
            setState('loading');
        } else if (errorMessage) {
            setState('error');
        } else if (successMessage) {
            // Only show success if explicitly provided via prop
            setState('success');
        } else {
            // Default state - no success, no error
            setState('default');
        }
    }, [loading, errorMessage, successMessage]);

    const handleChange = (value: string) => {
        if (disabled || loading) return;

        const numericValue = value.replace(/[^0-9]/g, '');
        const newOtp = numericValue.slice(0, length);
        // Move the controlled caret with the edit; leaving the stale selection in
        // state until onSelectionChange arrives can snap the native caret backwards.
        const caret = selection.start + (selection.end - selection.start) + (newOtp.length - otp.length);
        const clampedCaret = Math.max(0, Math.min(caret, newOtp.length));
        setSelection({ start: clampedCaret, end: clampedCaret });
        setOtp(newOtp);
        onChangeText?.(newOtp);

        // Clear error state when user starts typing
        if (errorMessage && !loading) {
            setState('default');
        }

        if (newOtp.length === length && !loading) {
            // Native: dismiss keyboard after last digit.
            // Web + Enter-submit: keep focus so typing/Enter still work after fill.
            if (Platform.OS !== 'web' || !onEnterSubmit) {
                Keyboard.dismiss();
            }

            // Trigger onComplete callback immediately - parent will handle loading state
            // Use minimal delay just to ensure UI has updated with the last digit
            requestAnimationFrame(() => {
                onComplete?.(newOtp);
            });
        }
    };

    /** Tapping a filled box selects that one digit so typing replaces it in place;
     * tapping past the entered digits just moves the caret to the end (nothing
     * there to edit yet). */
    const handleBoxPress = (index: number) => {
        if (disabled || loading) return;
        const input = inputRef.current;
        // Native: the hidden input can stay "focused" after the keyboard was hidden
        // (Android back/swipe, iOS modal dismiss). focus() is then a no-op and the
        // keypad never reopens — blur first so the refocus re-shows the keyboard.
        if (Platform.OS !== 'web' && input?.isFocused()) {
            input.blur();
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            input?.focus();
        }
        onFocus?.();
        if (index < otp.length) {
            setSelection({ start: index, end: index + 1 });
        } else {
            setSelection({ start: otp.length, end: otp.length });
        }
    };

    const handleLongPress = (event: any) => {
        if (disabled || loading) return;
        // Get position from event
        const { pageX, pageY } = event.nativeEvent;
        setContextMenuPosition({ x: pageX, y: pageY });
        setContextMenuVisible(true);
    };

    const handlePaste = async () => {
        setContextMenuVisible(false);
        if (disabled || loading) return;

        try {
            const clipboardText = await Clipboard.getStringAsync();
            if (!clipboardText) {
                logger.debug('OTPInput', 'Clipboard is empty');
                return;
            }

            // Extract numeric OTP from clipboard text
            const numericMatch = clipboardText.match(/\d+/g);
            if (!numericMatch || numericMatch.length === 0) {
                logger.debug('OTPInput', 'No numeric OTP found in clipboard');
                return;
            }

            // Find the first sequence that matches the OTP length
            let otpFromClipboard = '';
            for (const match of numericMatch) {
                if (match.length === length) {
                    otpFromClipboard = match;
                    break;
                } else if (match.length > length) {
                    otpFromClipboard = match.slice(0, length);
                    break;
                }
            }

            // If no exact match, try to find OTP pattern
            if (!otpFromClipboard) {
                const otpPatterns = [
                    new RegExp(`\\b(\\d{${length}})\\b`, 'g'),
                    new RegExp(`(?:otp|code|pin)[:=\\s]*(\\d{${length}})`, 'i'),
                    new RegExp(`(\\d{${length}})(?:\\s|$|\\D)`, 'g'),
                ];

                for (const pattern of otpPatterns) {
                    const match = pattern.exec(clipboardText);
                    if (match && match[1]) {
                        otpFromClipboard = match[1];
                        break;
                    }
                }
            }

            if (otpFromClipboard && otpFromClipboard.length === length) {
                logger.info('OTPInput', 'OTP pasted from clipboard', { length: otpFromClipboard.length });
                setOtp(otpFromClipboard);
                setSelection({ start: otpFromClipboard.length, end: otpFromClipboard.length });
                onChangeText?.(otpFromClipboard);
                // Trigger onComplete if OTP is complete
                if (onComplete) {
                    requestAnimationFrame(() => {
                        onComplete(otpFromClipboard);
                    });
                }
            }
        } catch (error) {
            logger.error('OTPInput', 'Failed to paste from clipboard', error);
        }
    };

    // Active box now tracks the real cursor/selection position (so a tapped box
    // is correctly highlighted), clamped to the last box once selection.start
    // reaches the end — same as the old otp.length-based default.
    const activeIndex = isFocused ? Math.min(selection.start, length - 1) : -1;

    const getBorderColor = (index: number) => {
        if (state === 'loading') return colors.outlineActive;
        if (state === 'error') return colors.outlineError;
        if (state === 'success') return colors.outlineSuccess;

        const isActive = index === activeIndex;
        const isFilled = index < otp.length;

        if (isActive) return '#922A22';
        if (isFilled) return colors.fontPrimary;
        
        return 'transparent'; // Empty box has no border (or matches background)
    };
    
    const getBackgroundColor = (index: number) => {
        const isActive = index === activeIndex;
        const isFilled = index < otp.length;
        
        if (isActive || isFilled) return colors.backgroundLevel1;
        return colors.backgroundLevel2;
    };

    // expose helpers in future if needed

    const getStatusMessage = () => {
        if (state === 'error' && errorMessage) {
            return errorMessage;
        }
        if (state === 'success' && successMessage) {
            return successMessage;
        }
        return null;
    };

    const getStatusColor = () => {
        switch (state) {
            case 'error':
                return errorTextColor ?? colors.fontErrorText;
            case 'success':
                return colors.fontSuccessText;
            default:
                return colors.fontSecondary;
        }
    };

    const styles = StyleSheet.create({
        container: {
            width: '100%',
        },
        labelContainer: {
            marginBottom: 8,
            alignItems: 'center',
        },
        label: {
            fontSize: 14,
            fontWeight: '500',
            color: colors.fontPrimary,
            fontFamily: 'Matter',
            textAlign: 'center',
        },
        otpContainer: {
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap,
            paddingHorizontal: 16,
            marginBottom: 8,
            position: 'relative',
        },
        inputBox: {
            width: boxSize,
            height: boxSize,
            borderRadius: boxRadius,
            borderWidth: 1,
            justifyContent: 'center',
            alignItems: 'center',
            paddingHorizontal: 16,
            paddingVertical: 16,
        },
        input: {
            fontSize,
            fontWeight: '600',
            color: colors.fontPrimary,
            fontFamily: 'Matter',
            textAlign: 'center',
            textAlignVertical: 'center',
            width: '100%',
            height: '100%',
            padding: 0,
            margin: 0,
            lineHeight: fontSize,
            includeFontPadding: false,
        },
        statusContainer: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingHorizontal: 16,
            gap: 8,
        },
        statusIcon: {
            width: 12,
            height: 12,
        },
        statusMessage: {
            fontSize: 13,
            fontWeight: '400',
            fontFamily: 'Matter',
            flex: 1,
        },
        hiddenInput: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0,
            zIndex: 0,
            fontSize: 16,
        },
        contextMenuOverlay: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
        contextMenu: {
            position: 'absolute',
            minWidth: 120,
            borderRadius: 8,
            paddingVertical: 4,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 4,
            elevation: 5,
        },
        contextMenuItem: {
            paddingHorizontal: 16,
            paddingVertical: 12,
        },
        contextMenuItemContent: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
        },
        contextMenuText: {
            fontSize: 16,
            fontFamily: 'Matter',
        },
    });

    const statusMessage = getStatusMessage();

    return (
        <View style={styles.container}>
            {label && (
                <View style={styles.labelContainer}>
                    <Text style={styles.label}>{label}</Text>
                </View>
            )}

            <View style={styles.otpContainer}>
                {Array.from({ length }).map((_, index) => (
                    <Pressable
                        key={index}
                        style={[styles.inputBox, { 
                            borderColor: getBorderColor(index), 
                            backgroundColor: getBackgroundColor(index),
                            zIndex: 10 
                        }]}
                        onPress={(e) => {
                            e.stopPropagation();
                            handleBoxPress(index);
                        }}
                        onLongPress={(e) => {
                            e.stopPropagation();
                            handleLongPress(e);
                        }}
                        delayLongPress={300}
                    >
                        <View style={{ position: 'relative', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', overflow: 'visible' }}>
                            {/* Blinking Cursor for active box */}
                            {isFocused && index === activeIndex && (
                                <View style={{ position: 'absolute', marginLeft: otp[index] ? 18 : 0, width: 2, height: 24, backgroundColor: '#922A22' }} />
                            )}
                            <Text style={styles.input} pointerEvents="none">{secure && otp[index] ? '*' : (otp[index] || '')}</Text>
                        </View>
                    </Pressable>
                ))}
                <TextInput
                    ref={inputRef}
                    style={styles.hiddenInput}
                    value={otp}
                    onChangeText={handleChange}
                    selection={selection}
                    onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
                    onFocus={() => {
                        setIsFocused(true);
                        onFocus?.();
                    }}
                    onBlur={() => setIsFocused(false)}
                    keyboardType="numeric"
                    maxLength={length}
                    editable={!disabled && !loading}
                    showSoftInputOnFocus={focusable}
                    textContentType="oneTimeCode"
                    autoComplete={type === 'phone' ? 'sms-otp' : 'one-time-code'}
                    autoCorrect={false}
                    autoCapitalize="none"
                    spellCheck={false}
                    caretHidden={true}
                    underlineColorAndroid="transparent"
                    autoFocus={autoFocus}
                    returnKeyType="done"
                    onSubmitEditing={() => {
                        if (
                            Platform.OS === 'web' &&
                            onEnterSubmit &&
                            !disabled &&
                            !loading &&
                            otp.length === length &&
                            !enterSubmitGuardRef.current
                        ) {
                            enterSubmitGuardRef.current = true;
                            onEnterSubmit();
                            return;
                        }
                        Keyboard.dismiss();
                    }}
                    importantForAutofill="yes"
                    pointerEvents="none"
                />
            </View>

            {/* Context Menu Modal */}
            <Modal
                visible={contextMenuVisible}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setContextMenuVisible(false)}
            >
                <TouchableOpacity
                    style={styles.contextMenuOverlay}
                    activeOpacity={1}
                    onPress={() => setContextMenuVisible(false)}
                >
                    <View style={[styles.contextMenu, {
                        top: contextMenuPosition.y - 40,
                        left: contextMenuPosition.x - 60,
                        backgroundColor: colors.backgroundLevel1,
                    }]}>
                        <TouchableOpacity
                            style={styles.contextMenuItem}
                            onPress={handlePaste}
                        >
                            <View style={styles.contextMenuItemContent}>
                                <PasteIcon size={18} color={colors.fontPrimary} />
                                <Text style={[styles.contextMenuText, { color: colors.fontPrimary }]}>
                                    {t('otp.pasteOTP')}
                                </Text>
                            </View>
                        </TouchableOpacity>
                    </View>
                </TouchableOpacity>
            </Modal>

            {statusMessage && (
                <View style={styles.statusContainer}>
                    {state === 'error' && (
                        <View style={styles.statusIcon}>
                            <CircleXIcon size={16} color={colors.fontErrorText} />
                        </View>
                    )}
                    {state === 'success' && (
                        <View style={styles.statusIcon}>
                            <Text style={{ color: colors.fontSuccessText, fontSize: 12 }}>✓</Text>
                        </View>
                    )}
                    <Text style={[styles.statusMessage, { color: getStatusColor() }]}>{statusMessage}</Text>
                </View>
            )}
        </View>
    );
};

export default OTPInput;